import type { JcRole, JcProductRole, JcConfiguratorFilterOptionSetting } from "@/types/jcFilterSystem"

// Source-of-truth fallback list, 1:1 with the backend UsageRole::ALL. The admin
// UI normally fetches the selectable roles from GET /backend/usage-roles
// (useJcUsageRolesStore); this list is used only until that endpoint answers,
// and as the canonical set for validating/normalizing incoming data.
export const DEFAULT_JC_ROLES: JcRole[] = ["player", "goalkeeper", "coach"]

export function isJcRole(value: unknown): value is JcRole {
  return typeof value === "string" && (DEFAULT_JC_ROLES as string[]).includes(value)
}

/**
 * Normalize a product's roles into a clean, de-duplicated string array.
 * Accepts the `displayGroups` association (DAL child shape `[{ role }]`), a plain
 * string array, or — for not-yet-migrated products — derives from the
 * deprecated `usageRole` / `goalkeeper` fields ("universal" → both).
 */
export function normalizeProductRoles(product: { displayGroups?: Array<JcProductRole | string> | null; usageRole?: string | null; goalkeeper?: boolean | null }): JcRole[] {
  const fromRoles = (product?.displayGroups ?? []).map((r) => (typeof r === "string" ? r : r?.role)).filter(isJcRole)
  if (fromRoles.length > 0) return Array.from(new Set(fromRoles))

  // Backwards-compat: derive from the deprecated single-valued usageRole / goalkeeper.
  if (product?.usageRole === "universal") return ["player", "goalkeeper"]
  if (product?.usageRole === "goalkeeper" || product?.goalkeeper) return ["goalkeeper"]
  if (product?.usageRole === "player") return ["player"]
  return []
}

/**
 * Resolve the effective per-role minimum quantities for a filter option setting.
 * Prefers the new `minRoleQuantities` map; falls back to the deprecated
 * min*Quantity columns for legacy rows that were never backfilled.
 */
export function resolveMinRoleQuantities(
  setting: Pick<JcConfiguratorFilterOptionSetting, "minRoleQuantities" | "minPlayerQuantity" | "minGoalkeeperQuantity"> | undefined | null
): Record<string, number> {
  const map = setting?.minRoleQuantities
  if (map && Object.keys(map).length > 0) {
    const cleaned: Record<string, number> = {}
    for (const [role, value] of Object.entries(map)) {
      if (isJcRole(role) && Number.isInteger(value) && value > 0) cleaned[role] = value
    }
    return cleaned
  }
  // Legacy fallback (only player & goalkeeper had columns).
  const fallback: Record<string, number> = {}
  if ((setting?.minPlayerQuantity ?? 0) > 0) fallback.player = setting!.minPlayerQuantity as number
  if ((setting?.minGoalkeeperQuantity ?? 0) > 0) fallback.goalkeeper = setting!.minGoalkeeperQuantity as number
  return fallback
}

/** Sum of all per-role minimums — the derived total minimum quantity. */
export function totalMinQuantity(map: Record<string, number>): number {
  return Object.values(map).reduce((sum, n) => sum + (n > 0 ? n : 0), 0)
}

/**
 * Build the DAL-child write payload for a product's `displayGroups` association.
 * Reuses the existing association-row `id` for roles the product already has,
 * so the DAL upserts (matches by primary key) instead of inserting a fresh row
 * and hitting the unique (product_id, role) constraint. New roles are sent
 * without an id so they get inserted.
 */
export function buildRolesWritePayload(product: { displayGroups?: Array<JcProductRole | string> | null }, desired: JcRole[]): Array<{ id?: string; role: JcRole }> {
  const existingIdByRole: Record<string, string> = {}
  for (const r of product?.displayGroups ?? []) {
    if (r && typeof r === "object" && r.id && isJcRole(r.role)) existingIdByRole[r.role] = r.id
  }
  return desired.map((role) => (existingIdByRole[role] ? { id: existingIdByRole[role], role } : { role }))
}

/**
 * Read the raw key of a `displayGroups` entry — either a bare string key or the
 * DAL-child object's `role`/key column. Garment keys live in the same column as
 * roles now that the product carries one flat, mixed key list.
 */
function displayGroupKey(entry: JcProductRole | string | { role?: string } | null | undefined): string | undefined {
  if (typeof entry === "string") return entry
  return (entry as { role?: string } | null)?.role
}

/**
 * Pull a product's assigned garment key out of its mixed `displayGroups` list.
 * Only keys present in the catalog's garment set (kind="garment") qualify, so
 * role keys are never mistaken for a garment. Returns the first match or null.
 */
export function normalizeProductGarment(product: { displayGroups?: Array<JcProductRole | string> | null }, garmentKeys: string[]): string | null {
  const garments = new Set(garmentKeys)
  for (const entry of product?.displayGroups ?? []) {
    const key = displayGroupKey(entry)
    if (key && garments.has(key)) return key
  }
  return null
}

/**
 * Build the flat `displayGroups` write payload — a de-duplicated list of plain
 * string keys mixing usage roles and (optionally) one garment key. The backend
 * resolves each key to its catalog entry and reconciles the product's
 * associations server-side.
 */
export function buildDisplayGroupKeys(roles: JcRole[], garmentKey?: string | null): string[] {
  const keys: string[] = [...roles]
  if (garmentKey) keys.push(garmentKey)
  return Array.from(new Set(keys))
}
